import { FC } from 'react';

import { css } from '@emotion/react';
import { ToucanComponents, ToucanColors, IconName } from '@jointoucan/toucan-design';
import { Box, Skeleton, useTheme } from '@mui/material';
import { useTranslation } from 'next-i18next';

import Divider from '~/components/shared/Divider';
import { getFirstName } from '~/helpers/utility';
import { useLoggedInUser } from '~/hooks/useLoggedInUser';

import Avatar from './Avatar';
import NavLink from './NavLink';

const { Typography } = ToucanComponents;

interface Link {
  url: string;
  iconName: IconName;
  text: string;
  isVisible?: boolean;
  isExternalUrl?: boolean;
}

interface MenuProps {
  links: Link[];
}

const Menu: FC<MenuProps> = ({ links }) => {
  const theme = useTheme();
  const { loggedInUser, selectedLanguage, isLoggedIn } = useLoggedInUser();
  const { t } = useTranslation();

  return loggedInUser ? (
    <>
      <div
        css={css`
          display: flex;
          margin-bottom: ${theme.spacing(3)};
          ${theme.breakpoints.up('sm')} {
            flex-direction: column;
            align-items: center;
          }
        `}
      >
        <Avatar
          userEmail={loggedInUser.email ?? undefined}
          language={selectedLanguage}
          css={css`
            margin-right: ${theme.spacing(2)};
            ${theme.breakpoints.up('sm')} {
              margin-right: 0;
            }
          `}
        />
        <Typography
          variant="md"
          font="secondary"
          color={ToucanColors.gray[600]}
          css={css`
            ${theme.breakpoints.up('sm')} {
              text-align: center;
            }
          `}
        >
          {getFirstName(loggedInUser.name)}
        </Typography>
      </div>
      <Divider
        css={css`
          margin-bottom: ${theme.spacing(2)};
        `}
      />

      {links.map(({ url, iconName, text, isVisible = true, isExternalUrl = false }) => {
        if (!isVisible) {
          return null;
        }

        return (
          <NavLink href={url} iconName={iconName} key={text} isExternalUrl={isExternalUrl}>
            {text}
          </NavLink>
        );
      })}

      {isLoggedIn && (
        <>
          <Divider
            css={css`
              margin-bottom: ${theme.spacing(2)};
            `}
          />
          <NavLink href="/sign-out" iconName="times" data-testid="dashboard-menu-logout-button">
            {t('sharedNavLogOut')}
          </NavLink>
        </>
      )}
    </>
  ) : (
    <Box width={250}>
      <Skeleton animation="wave" width="100%" height={60} />
      <Skeleton animation="wave" width="100%" height={60} />
      <Skeleton animation="wave" width="100%" height={60} />
    </Box>
  );
};

export default Menu;
